import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { searchDiagnoses } from './diagnosisData';

const SearchableDiagnosisDropdown = ({ label, value, code, onSelect, required, helperText, diagnosisList, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDiagnoses, setFilteredDiagnoses] = useState([]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Use custom list if provided, otherwise use full list via searchDiagnoses
  const searchFn = useCallback((term) => {
    if (diagnosisList) {
      if (!term) return diagnosisList;
      const t = term.toLowerCase();
      return diagnosisList.filter(d =>
        d.name.toLowerCase().includes(t) || d.code.toLowerCase().includes(t)
      );
    }
    return searchDiagnoses(term);
  }, [diagnosisList]);

  useEffect(() => {
    setFilteredDiagnoses(searchFn(searchTerm));
  }, [searchTerm, searchFn]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (diagnosis) => {
    onSelect(diagnosis.name, diagnosis.code);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onSelect('', '');
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setFilteredDiagnoses(searchFn(searchTerm));
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div ref={dropdownRef} className="relative">
        {/* Selected value display / Search input */}
        <div className="relative">
          {value && !isOpen ? (
            <div
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white flex items-center justify-between cursor-pointer hover:border-blue-400 transition-colors"
              onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
              data-testid={`diagnosis-selected-${label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex-1 min-w-0">
                <span className="text-gray-900 block truncate">{value}</span>
                <span className="text-xs text-blue-600 font-mono">{code}</span>
              </div>
              <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleClear(); }}
                  className="p-0.5 hover:bg-gray-100 rounded"
                  data-testid={`diagnosis-clear-${label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleInputFocus}
                placeholder="Type to search diagnoses..."
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid={`diagnosis-search-${label.replace(/\s+/g, '-').toLowerCase()}`}
              />
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          )}
        </div>

        {/* Dropdown list */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid={`diagnosis-dropdown-${label.replace(/\s+/g, '-').toLowerCase()}`}>
            {filteredDiagnoses.length > 0 ? (
              filteredDiagnoses.map((d) => (
                <button
                  key={`${d.name}-${d.code}`}
                  type="button"
                  onClick={() => handleSelect(d)}
                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-50 last:border-b-0 ${
                    value === d.name ? 'bg-blue-50 font-medium' : ''
                  }`}
                >
                  <span className="text-gray-900 text-sm block">{d.name}</span>
                  <span className="text-xs text-blue-600 font-mono">{d.code}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                No diagnoses found for "{searchTerm}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Auto-populated code + description display */}
      {code && !isOpen && (
        <div className="bg-blue-50 px-3 py-2 rounded border border-blue-200 space-y-0.5" data-testid={`diagnosis-info-${label.replace(/\s+/g, '-').toLowerCase()}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-blue-700">ICD/DSM Code:</span>
            <span className="text-sm font-mono font-bold text-blue-900" data-testid={`diagnosis-code-${label.replace(/\s+/g, '-').toLowerCase()}`}>{code}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-blue-700">Description:</span>
            <span className="text-sm text-blue-900" data-testid={`diagnosis-desc-${label.replace(/\s+/g, '-').toLowerCase()}`}>{value}</span>
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
      {helperText && !error && (
        <p className="text-xs text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

export default SearchableDiagnosisDropdown;
